import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { sustainabilityPillars } from '../data/sbbData';
import { Play, Leaf, Droplets, Users, ShieldCheck, ArrowRight } from 'lucide-react';
import PageHero from '../components/PageHero';

export default function Sustainability() {
  const { lang, t } = useLanguage();

  return (
    <div className="sustainability-page">
      {/* Page Hero */}
      <PageHero eyebrow="Komitmen Masa Depan" title={t('nav_sustainability')} subtitle="Membangun Indonesia berkelanjutan melalui solusi beton rendah karbon, efisiensi sirkularitas air, dan pemberdayaan masyarakat." image="/reference-images/sustainability.webp" />

      {/* Breadcrumbs */}
      <div className="breadcrumb-wrapper">
        <div className="container">
          <ul className="breadcrumb-list">
            <li><Link to="/">{t('nav_home')}</Link></li>
            <li><span className="separator">/</span></li>
            <li><span className="current">{t('nav_sustainability')}</span></li>
          </ul>
        </div>
      </div>

      {/* Video Feature Section (Section 5.9) */}
      <section className="section">
        <div className="container">
          <div className="grid-2 align-center">
            <div>
              <span className="accent-bar"></span>
              <span className="eyebrow">Strategi Net-Zero SIG</span>
              <h2 className="section-title">Inovasi Beton Hijau untuk Generasi Mendatang</h2>
              <p className="about-desc-text">
                Sebagai bagian dari SIG, Solusi Bangun Beton berkomitmen menurunkan emisi karbon spesifik dalam proses pembuatan dan pengangkutan beton siap pakai.
              </p>
              <p className="about-desc-text">
                Melalui penggantian semen konvensional dengan Semen Blended Ramah Lingkungan serta optimalisasi rute armada armada mixer, kami menciptakan solusi beton yang tangguh sekaligus menjaga kelestarian alam.
              </p>
            </div>

            <div className="video-feature-box">
              <img 
                src="/reference-images/sustainability.webp" 
                alt="Video Keberlanjutan SBB" 
                className="img-rounded-shadow"
              />
              <a href="#play-video" onClick={(e) => { e.preventDefault(); alert('Memutar Video Komitmen Keberlanjutan Solusi Bangun Beton...'); }} className="play-btn-circle">
                <Play size={28} fill="#ffffff" color="#ffffff" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Pillars of Sustainability (Section 5.9) */}
      <section className="section section-subtle">
        <div className="container">
          <div className="section-header text-center">
            <span className="accent-bar"></span>
            <span className="eyebrow">Kerangka Kerja ESG</span>
            <h2 className="section-title">3 Pilar Utama Keberlanjutan SBB</h2>
          </div>

          <div className="grid-3">
            <div className="sbb-card pillar-card">
              <div className="pillar-icon-box">
                <Leaf size={32} color="var(--color-brand-primary)" />
              </div>
              <h3 className="sbb-card-title" style={{ fontSize: '18px' }}>1. Solusi Berkelanjutan</h3>
              <p className="sbb-card-desc">{sustainabilityPillars[0].desc}</p>
            </div>

            <div className="sbb-card pillar-card">
              <div className="pillar-icon-box">
                <Droplets size={32} color="var(--color-brand-primary)" />
              </div>
              <h3 className="sbb-card-title" style={{ fontSize: '18px' }}>2. Iklim & Sirkularitas</h3>
              <p className="sbb-card-desc">{sustainabilityPillars[1].desc}</p>
            </div>

            <div className="sbb-card pillar-card">
              <div className="pillar-icon-box">
                <Users size={32} color="var(--color-brand-primary)" />
              </div>
              <h3 className="sbb-card-title" style={{ fontSize: '18px' }}>3. Karyawan & Komunitas</h3>
              <p className="sbb-card-desc">{sustainabilityPillars[2].desc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Green Certification Highlights */}
      <section className="section">
        <div className="container text-center">
          <span className="accent-bar" style={{ margin: '0 auto 12px auto' }}></span>
          <h2 className="section-title">Standardisasi & Penghargaan Lingkungan</h2>
          <p className="section-subtitle">Pengakuan pemerintah dan lembaga independen terhadap pengelolaan lingkungan SBB.</p>

          <div className="cert-highlights-row">
            <div className="highlight-badge-card">
              <ShieldCheck size={28} color="var(--color-brand-primary)" />
              <div>
                <strong>PROPER Biru KemenLHK</strong>
                <p>Taat Pengelolaan Lingkungan</p>
              </div>
            </div>

            <div className="highlight-badge-card">
              <Leaf size={28} color="var(--color-brand-primary)" />
              <div>
                <strong>Sertifikat Ekolabel</strong>
                <p>Beton Ramah Lingkungan</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Band */}
      <section className="cta-band-section">
        <div className="container text-center">
          <h2>Tertarik Menggunakan Beton Ramah Lingkungan untuk Proyek Anda?</h2>
          <p style={{ marginTop: '8px', marginBottom: '24px', color: 'var(--color-text-muted)' }}>
            Tim kami siap membantu perhitungan pengurangan jejak karbon konstruksi Anda.
          </p>
          <Link to="/kontak-kami" className="btn btn-primary">
            <span>Konsultasi Beton Hijau</span>
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <style>{`
        .video-feature-box {
          position: relative;
        }

        .pillar-card {
          padding: 32px 24px;
          text-align: center;
          align-items: center;
        }

        .pillar-icon-box {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background-color: var(--color-surface-subtle);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
        }

        .cert-highlights-row {
          display: flex;
          justify-content: center;
          gap: 24px;
          margin-top: 36px;
        }

        .highlight-badge-card {
          background-color: var(--color-surface-subtle);
          border: 1px solid var(--color-border);
          padding: 20px 30px;
          border-radius: var(--border-radius-md);
          display: flex;
          align-items: center;
          gap: 16px;
          text-align: left;
        }

        .highlight-badge-card strong {
          display: block;
          font-size: 16px;
          color: var(--color-ink);
        }

        .highlight-badge-card p {
          font-size: 13px;
          color: var(--color-text-muted);
        }

        @media (max-width: 768px) {
          .cert-highlights-row {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
}
